import React from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../context/GameContext";
import socketService from "../services/socketService";
import { PartyPopper, Crown, Gamepad2, Home } from "lucide-react";

const MedalIcon = ({ place, size = 32 }) => {
  const colors = {
    1: { ribbon: "#dc2626", main: "#facc15", shine: "#fef08a", text: "#92400e", border: "#ca8a04" },
    2: { ribbon: "#2563eb", main: "#d1d5db", shine: "#f3f4f6", text: "#374151", border: "#9ca3af" },
    3: { ribbon: "#16a34a", main: "#f97316", shine: "#fdba74", text: "#7c2d12", border: "#c2410c" },
  };
  const c = colors[place] || colors[1];
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <path d="M14 4L20 18L12 14Z" fill={c.ribbon} opacity="0.9" />
      <path d="M26 4L20 18L28 14Z" fill={c.ribbon} opacity="0.7" />
      <circle cx="20" cy="24" r="13" fill={c.border} />
      <circle cx="20" cy="24" r="11" fill={c.main} />
      <circle cx="20" cy="24" r="8" fill={c.shine} opacity="0.3" />
      <text x="20" y="29" textAnchor="middle" fontSize="14" fontWeight="bold" fill={c.text}>{place}</text>
    </svg>
  );
};

const GameOver = () => {
  const { winners, roomId, resetGameState, launchNewRound } = useGame();
  const navigate = useNavigate();

  const handleBackHome = () => {
    socketService.leaveRoom();
    socketService.clearSession();
    resetGameState();
    navigate("/");
  };

  const handlePlayAgain = () => {
    launchNewRound();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-600 mb-2">
            <PartyPopper size={28} className="inline mr-2" /> Partie terminée !
          </h1>
          <p className="text-gray-600">
            Room : <span className="font-semibold">{roomId}</span>
          </p>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-center text-gray-800">
            Classement final
          </h2>
          <div className="space-y-3">
            {winners.map((winner, index) => (
              <div
                key={winner.id}
                className="flex items-center justify-between p-4 rounded-lg"
                style={{
                  background: index === 0
                    ? "linear-gradient(135deg, rgba(250,204,21,0.15) 0%, rgba(253,224,71,0.1) 100%)"
                    : index === 1
                      ? "linear-gradient(135deg, rgba(209,213,219,0.15) 0%, rgba(229,231,235,0.1) 100%)"
                      : index === 2
                        ? "linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(251,146,60,0.1) 100%)"
                        : "linear-gradient(135deg, rgba(167,139,250,0.1) 0%, rgba(129,140,248,0.1) 100%)",
                  border: index === 0
                    ? "2px solid #ca8a04"
                    : index === 1
                      ? "2px solid #9ca3af"
                      : index === 2
                        ? "2px solid #c2410c"
                        : "1px solid rgba(167,139,250,0.3)",
                }}
              >
                <div className="flex items-center gap-4">
                  <span className="flex items-center justify-center" style={{ width: 36, height: 36 }}>
                    {index < 3
                      ? <MedalIcon place={index + 1} size={36} />
                      : <span className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: "rgba(167,139,250,0.2)", color: "#7c3aed", border: "2px solid rgba(167,139,250,0.4)" }}>{winner.position}</span>}
                  </span>
                  <div>
                    <span className="text-xl font-bold text-gray-800">
                      {winner.pseudo}
                    </span>
                    {winner.character && (
                      <p className="text-sm italic text-gray-600">
                        {winner.character}
                      </p>
                    )}
                  </div>
                </div>
                {index === 0 && <span className="text-2xl"><Crown size={24} className="text-yellow-600" /></span>}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <button
            onClick={handlePlayAgain}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 transform hover:scale-105"
          >
            <Gamepad2 size={18} className="inline mr-1" /> Rejouer
          </button>
          <button
            onClick={handleBackHome}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 transform hover:scale-105"
          >
            <Home size={18} className="inline mr-1" /> Retour à l'accueil
          </button>
        </div>

        <div className="mt-6 p-4 bg-purple-50 rounded-lg text-center">
          <p className="text-gray-700">Merci d'avoir joué ! <Gamepad2 size={16} className="inline mr-1" /></p>
        </div>
      </div>
    </div>
  );
};

export default GameOver;
